function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function(r2) {
      return Object.getOwnPropertyDescriptor(e, r2).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), true).forEach(function(r2) {
      _defineProperty(e, r2, t[r2]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function(r2) {
      Object.defineProperty(e, r2, Object.getOwnPropertyDescriptor(t, r2));
    });
  }
  return e;
}
function _defineProperty(e, r, t) {
  return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: true, configurable: true, writable: true }) : e[r] = t, e;
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t)
    return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i)
      return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
import * as React from "react";
import produce from "immer";
import { clsx } from "clsx";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { SelectReact } from "common/components/select";
import { SelectTheme } from "common/components/select/constants";
import { QueryBlockReact } from "common/query-builder/query-block";
import { IncludeCustomPropertyType, MenuItemType } from "common/query-builder/query-block/properties-menu/types";
import { segmentationToPropertySelection } from "common/query-builder/query-block/segment-menu/util";
import { EntryPart, isEventBasedEntry } from "common/query-builder/query-entry-section/types";
import { WindowType } from "common/types/reports/query";
import { RowFilters } from "common/query-builder/query-entry-section/components/filters";
import { ReportName, SPECIAL_EVENTS } from "common/report/constants";
import { TimeUnitLabelTranslatable } from "common/widgets/date-range-picker/models/types";
import { MultiAttributionLookback } from "common/widgets/multi-attribution-lookback";
import { PropertyFilter } from "common/widgets/property-filter-menu/models/property-filter";
import { SelectorType } from "common/query-builder/query-block/constants";
import { MenuType } from "common/query-builder/query-block/types";
import { renameEvent } from "common/report/util/events";
import { useTranslation } from "common/i18n/hooks";
import { propertyItemToSegmentation } from "common/query-builder/query-block/properties-menu/util";
import { RevenueChangeBlock } from "common/query-builder/query-entry-section/components/revenue-change";
import { getPropertyName } from "common/query-builder/query-block/count-menu/util";
import { useCurrentProject } from "common/contexts/react/current-project-context";
import { assignDefaultTimeReference, DEFAULT_AGGREGATION_BEHAVIOR, DEFAULT_FREQUENCY_BEHAVIOR, generateAggregationsList, getAggregationLabelForBehavior, getColumnLabelForBehavior, isAggregateUserFreqByGroupSegmentation, isAggregationBehavior, isAggregationSegmentation, isEventBasedBehavioralSegmentation, isMultiAttributionSegmentation } from "./utils";
import { BehavioralStepSelectionSubItem } from "./behavioral-step-selection-subitem";
import { BehavioralLookbackWindowSubItem } from "./behavioral-lookback-window-subitem";
export function BehavioralTimeReference(props) {
  const currentSelection = props.selected;
  const currentWindowType = currentSelection.behavior.windowType;
  const aggUserFreqByGroup = isAggregateUserFreqByGroupSegmentation(currentSelection);
  const {
    reportName,
    stepCount,
    sectionName
  } = props.sectionProps;
  const {
    t
  } = useTranslation();
  const funnelTimeReferenceSelection = /* @__PURE__ */ React.createElement("div", { className: "subitem-select" }, /* @__PURE__ */ React.createElement(BehavioralStepSelectionSubItem, { selected: currentSelection, stepCount, onChange: props.onSelect }));
  const retentionTimeReferenceSelection = /* @__PURE__ */ React.createElement("div", { className: "subitem-select" }, /* @__PURE__ */ React.createElement(BehavioralLookbackWindowSubItem, { lookbackWindow: currentSelection.behavior, open: props.activePart === EntryPart.BehavioralLookbackWindow, onOpen: () => props.setActivePart(EntryPart.BehavioralLookbackWindow, "open"), onClose: () => props.setActivePart(EntryPart.BehavioralLookbackWindow, "close"), onChange: (window) => props.onSelect(_objectSpread(_objectSpread({}, currentSelection), {}, {
    behavior: _objectSpread(_objectSpread({}, currentSelection.behavior), window)
  })) }));
  const handleTimeReferenceSelection = (ev) => {
    const {
      value: windowType
    } = ev.detail.item;
    const selection = _objectSpread(_objectSpread({}, currentSelection), {}, {
      behavior: assignDefaultTimeReference(currentSelection.behavior, windowType)
    });
    props.onSelect(selection);
  };
  const getTimeReferenceLabel = (windowType) => {
    switch (windowType) {
      case WindowType.BETWEEN:
        return t("query-builder.behavior-interval.between-funnel-steps");
      case WindowType.AFTER:
        return t("query-builder.behavior-interval.after-step-1");
    }
    return t("query-builder.behavior-interval.per-interval");
  };
  const getSectionItems = () => {
    const sectionItems = [{
      label: t("query-builder.behavior-interval.per-interval"),
      value: WindowType.PER_INTERVAL,
      isSelected: !currentWindowType
    }, {
      label: t("query-builder.behavior-interval.between-steps"),
      value: WindowType.BETWEEN,
      isSelected: currentWindowType === WindowType.BETWEEN
    }, {
      label: t("query-builder.behavior-interval.after-step-1"),
      value: WindowType.AFTER,
      isSelected: currentWindowType === WindowType.AFTER
    }];
    if (sectionName === "inline-filter" || aggUserFreqByGroup) {
      return sectionItems.filter((item) => item.value === WindowType.PER_INTERVAL);
    }
    return sectionItems;
  };
  if (!aggUserFreqByGroup && reportName === ReportName.Funnels) {
    return /* @__PURE__ */ React.createElement("div", { className: "subitem-text" }, /* @__PURE__ */ React.createElement("div", { className: "behavioral-time-reference" }, t("query-builder.behavior-interval.between-funnel-steps")), funnelTimeReferenceSelection);
  }
  if (!aggUserFreqByGroup && reportName === ReportName.Retention) {
    return /* @__PURE__ */ React.createElement("div", { className: "subitem-text" }, /* @__PURE__ */ React.createElement("div", { className: "behavioral-time-reference" }, t("query-builder.behavior-interval.after-step-1")), retentionTimeReferenceSelection);
  }
  return /* @__PURE__ */ React.createElement("div", { className: "subitem-text", "data-sentry-component": "BehavioralTimeReference", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement(SelectReact, { className: "subitem-select time-reference-select", elref: "timeReferenceSelect", disableSearch: true, menuWidth: 150, tabIndex: -1, theme: SelectTheme.QbSubitem, size: ButtonSize.Small, label: getTimeReferenceLabel(currentWindowType), sections: [{
    items: getSectionItems()
  }], onChange: handleTimeReferenceSelection, tooltip: props.tooltip, "data-sentry-element": "SelectReact", "data-sentry-source-file": "template.tsx" }), currentWindowType === WindowType.BETWEEN && funnelTimeReferenceSelection, currentWindowType === WindowType.AFTER && retentionTimeReferenceSelection);
}
export function BehavioralSubItem(props) {
  var _behavior$event, _behavior$event2;
  const currentProject = useCurrentProject().get().value;
  const i18n = useTranslation();
  const currentSelection = props.selected;
  const behavior = currentSelection.behavior;
  const isEventProp = isAggregationBehavior(behavior);
  const isListOfObjectProp = isEventProp && !!props.sectionProps.listProperty;
  const isEvent = !isEventProp;
  const handleAggregationSelection = (ev) => {
    const {
      value: aggregation
    } = ev.detail.item;
    const selection = addBehaviorToSegmentation(currentSelection, _objectSpread(_objectSpread({}, behavior), {}, {
      aggregationOperator: aggregation.operator
    }), i18n);
    props.onSelect(_objectSpread(_objectSpread({}, currentSelection), selection));
  };
  const sections = generateAggregationsList(behavior);
  const label = getAggregationLabelForBehavior(behavior);
  return /* @__PURE__ */ React.createElement("div", { className: "mp-query-sub-selector", "data-sentry-component": "BehavioralSubItem", "data-sentry-source-file": "template.tsx" }, isAggregationBehavior(behavior) && /* @__PURE__ */ React.createElement("div", { className: "subitem-text" }, /* @__PURE__ */ React.createElement(SelectReact, { className: "subitem-select", elref: "aggregatorSelect", disableSearch: true, tabIndex: -1, theme: SelectTheme.QbSubitem, size: ButtonSize.Small, open: props.activePart === EntryPart.Aggregator, label, sections, onClose: () => props.setActivePart(EntryPart.Aggregator, "close"), onOpen: () => props.setActivePart(EntryPart.Aggregator, "open"), onChange: handleAggregationSelection })), isEvent && /* @__PURE__ */ React.createElement(PropertySelector, { ...props, selected: props.selected, displayCohorts: props.displayCohorts, isForFilter: props.isForFilter, isInner: true, activePart: props.activePart, setActivePart: props.setActivePart, queryBlockConfig: _objectSpread(_objectSpread({}, props.queryBlockConfig), {}, {
    type: MenuType.Events,
    selected: (_behavior$event = behavior === null || behavior === void 0 ? void 0 : behavior.event) !== null && _behavior$event !== void 0 ? _behavior$event : {
      custom: false,
      label: "",
      value: null
    }
  }), onSelect: (selectedItem) => {
    const segment = props.selected;
    const handledEvent = getPropertySelectionFromEventUpdate(selectedItem, segment, i18n);
    props.onSelect(handledEvent);
  } }), isEventProp && !isListOfObjectProp && /* @__PURE__ */ React.createElement(PropertySelector, { ...props, selected: props.selected, displayCohorts: props.displayCohorts, isForFilter: props.isForFilter, isInner: true, activePart: props.activePart, setActivePart: props.setActivePart, queryBlockConfig: _objectSpread(_objectSpread({}, props.queryBlockConfig), {}, {
    type: MenuType.Breakdown,
    showSpecialDatePropertyUnit: true,
    selected: {
      event: (_behavior$event2 = behavior === null || behavior === void 0 ? void 0 : behavior.event) !== null && _behavior$event2 !== void 0 ? _behavior$event2 : {
        custom: false,
        label: "",
        value: null
      },
      property: behavior === null || behavior === void 0 ? void 0 : behavior.property
    }
  }), onSelect: (selectedItem) => {
    const segment = props.selected;
    const handledEvent = getPropertySelectionFromEventPropUpdate(selectedItem, segment, i18n);
    props.onSelect(handledEvent);
  } }), props.showBehaviorTimeReference !== false && /* @__PURE__ */ React.createElement(BehavioralTimeReference, { ...props, selected: props.selected, onSelect: props.onSelect, activePart: props.activePart, setActivePart: props.setActivePart, currentProject }));
}
export function MultiAttributionLookbackSubItem(props) {
  const {
    t,
    print
  } = useTranslation();
  if (!isMultiAttributionSegmentation(props.selected)) {
    return null;
  }
  const {
    window
  } = props.selected.behavior.dateRange;
  const dateRangeLabel = t("query-builder.attribution.lookback-duration", {
    duration: "".concat(window.value, " ").concat(print(TimeUnitLabelTranslatable[window.unit](1)))
  });
  const isOpen = props.activePart === EntryPart.MultiAttributionLookback;
  return /* @__PURE__ */ React.createElement("div", { className: "subitem-text", "data-sentry-component": "MultiAttributionLookbackSubItem", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement(SelectReact, { className: "subitem-select", elref: "multiAttributionLookbackSelect", theme: SelectTheme.QbSubitem, size: ButtonSize.Small, label: dateRangeLabel, open: isOpen, onMenuChange: (ev) => props.setActivePart(EntryPart.MultiAttributionLookback, ev.detail.isOpen ? "open" : "close"), "data-sentry-element": "SelectReact", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement("div", { slot: "body" }, isOpen && /* @__PURE__ */ React.createElement(MultiAttributionLookback, { lookbackWindow: props.selected.behavior.dateRange.window, projectId: props.sectionProps.projectId, onCancel: () => props.setActivePart(EntryPart.MultiAttributionLookback, "close"), onChange: (lookbackWindow) => {
    const updatedSelection = produce(props.selected, (draft) => {
      draft.behavior.dateRange.window = _objectSpread({}, lookbackWindow);
    });
    props.onSelect(updatedSelection);
  } }))));
}
function addBehaviorToSegmentation(segmentation, behavior, i18n) {
  const defaultBehavior = isAggregationBehavior(behavior) ? DEFAULT_AGGREGATION_BEHAVIOR : DEFAULT_FREQUENCY_BEHAVIOR;
  return produce(segmentation, (draft) => {
    const behaviorDraft = _objectSpread(_objectSpread(_objectSpread({}, defaultBehavior), draft.behavior), behavior);
    draft.behavior = behaviorDraft;
    draft.propertyName = i18n.print(getColumnLabelForBehavior(behaviorDraft));
  });
}
function getPropertySelectionFromEventPropUpdate(selectedEventPropertyItem, currentSelection, i18n) {
  const {
    event,
    property
  } = selectedEventPropertyItem.item;
  return addBehaviorToSegmentation(currentSelection, _objectSpread(_objectSpread({}, currentSelection.behavior), {}, {
    event,
    property
  }), i18n);
}
function getPropertySelectionFromEventUpdate(eventItem, currentSelection, i18n) {
  const item = eventItem.item;
  const event = item;
  const overrides = {};
  if (event.value === SPECIAL_EVENTS.SELECTED_STEP) {
    overrides.startStepNumber = 0;
  }
  return addBehaviorToSegmentation(currentSelection, _objectSpread(_objectSpread({}, currentSelection.behavior), {}, {
    event: {
      label: event.label,
      value: event.value
    }
  }, overrides), i18n);
}
function getInnerLabelRenderer(segment) {
  var _behavior$event3;
  const {
    print
  } = useTranslation();
  if (isMultiAttributionSegmentation(segment) || !isAggregationSegmentation(segment) || !(segment !== null && segment !== void 0 && segment.behavior.property)) {
    return null;
  }
  const behavior = segment.behavior;
  const event = renameEvent(behavior === null || behavior === void 0 || (_behavior$event3 = behavior.event) === null || _behavior$event3 === void 0 ? void 0 : _behavior$event3.label.toString());
  const propertyName = getPropertyName(behavior.property, {
    includeListItemGroup: true,
    showSpecialDatePropertyUnit: true
  });
  return () => /* @__PURE__ */ React.createElement(React.Fragment, null, print(propertyName), /* @__PURE__ */ React.createElement("span", { className: "regular" }, " on "), event);
}
export function PropertySelector(props) {
  var _props$entry$prefixLa, _props$sectionProps$p, _props$selectorIcon;
  const {
    t,
    print
  } = useTranslation();
  const entryPart = props.isInner ? EntryPart.SubValue : EntryPart.Value;
  const labelRenderer = props.isInner ? getInnerLabelRenderer(props.selected) : null;
  return /* @__PURE__ */ React.createElement(QueryBlockReact, { className: clsx("entry-selector", {
    "is-disabled": props.entry.readOnlyEvent,
    "is-placeholder": props.isPlaceholder && props.selected === null,
    "subitem-style": props.isInner
  }), showSessionEvent: true, selected: props.isForFilter ? _objectSpread({}, props.selected) : segmentationToPropertySelection(props.selected), elref: "entrySelector", applyButtonText: props.sectionProps.sectionTitle && t("query-builder.add-item", {
    item: print(props.sectionProps.sectionTitle)
  }), disableValueSelection: true, minSelection: 1, prefixLabel: (_props$entry$prefixLa = props.entry.prefixLabel) !== null && _props$entry$prefixLa !== void 0 ? _props$entry$prefixLa : void 0, allDataGroups: props.sectionProps.dataGroupsEnabled, cohortMultiselect: true, dataGroupId: props.sectionProps.dataGroupId, metricLevelDataGroups: props.sectionProps.metricLevelDataGroups, disabledItems: props.sectionProps.disabledItems, enableEnhancedListFilters: props.sectionProps.enableEnhancedListFilters, enableWorkspaceFilterRestrictions: props.sectionProps.enableWorkspaceFilterRestrictions, eventNames: props.sectionProps.eventNames, excludeCustomEvents: props.sectionProps.excludeCustomEvents, excludeDimensionProperties: props.sectionProps.excludeDimensionProperties, hideEditControls: props.sectionProps.hideEditControls, hideEvents: props.sectionProps.hideEvents, includeCustomProperties: props.sectionProps.includeCustomProperties, showBorrowedProperties: props.sectionProps.showBorrowedProperties, inlineCohortsOverride: props.sectionProps.inlineCohortsOverride, isMultiAttribution: isMultiAttributionSegmentation(props.selected), projectHasCohorts: props.displayCohorts && props.sectionProps.projectHasCohorts, projectHasPeople: props.sectionProps.projectHasPeople, projectId: props.sectionProps.projectId, projectIsFreePlan: props.sectionProps.projectIsFreePlan, propertyCombineMode: (_props$sectionProps$p = props.sectionProps.propertyCombineMode) !== null && _props$sectionProps$p !== void 0 ? _props$sectionProps$p : void 0, readonly: props.sectionProps.readonly, selectorIcon: (_props$selectorIcon = props.selectorIcon) !== null && _props$selectorIcon !== void 0 ? _props$selectorIcon : "", selectorType: SelectorType.Button, showSpecialDatasetProperty: props.sectionProps.showSpecialDatasetProperty, supportB2bCohorts: props.sectionProps.supportB2bCohorts, trackName: props.sectionProps.trackName, userId: props.sectionProps.userId, workspaceId: props.sectionProps.workspaceId, autofocus: props.index === 0 && props.sectionProps.autofocus && props.isPlaceholder && !props.isFirstEntryStaged, disabled: props.sectionProps.disabled, open: props.isPlaceholder && props.activePart === entryPart, showAllTab: true, buttonSize: props.isInner && props.hasNewUI ? ButtonSize.Small : ButtonSize.Medium, theme: props.hasNewUI ? ButtonTheme.QueryBuilder : props.entry.readOnlyEvent ? ButtonTheme.Tertiary : ButtonTheme.Select, menuItemTypes: props.sectionProps.propertiesMenuItemTypes, typeFilter: props.sectionProps.propertiesMenuPropertyTypes, ...props.queryBlockConfig, labelRenderer, contextPanelParentElement: props.contextPanelParentElement, onClose: () => {
    props.setActivePart(entryPart, "close");
    props.onEntrySelectorDropdownStateChanged(false);
  }, onOpen: () => {
    props.setActivePart(entryPart, "open");
    props.onEntrySelectorDropdownStateChanged(true);
  }, onRemove: () => props.removeEntry(), onTrack: (ev) => props.trackEntrySelectorEvent(ev), onSelect: props.onSelect, "data-sentry-element": "QueryBlockReact", "data-sentry-component": "PropertySelector", "data-sentry-source-file": "template.tsx" });
}
export function BehavioralSubFilters(props) {
  var _behavior$event4, _behavior$filters;
  if (!isEventBasedBehavioralSegmentation(props.selected)) {
    return null;
  }
  const behavior = props.selected.behavior;
  return /* @__PURE__ */ React.createElement(RowFilters, { entry: props.entry, eventNames: (_behavior$event4 = behavior.event) !== null && _behavior$event4 !== void 0 && _behavior$event4.value ? [behavior.event.value] : [], index: props.index, filters: ((_behavior$filters = behavior.filters) === null || _behavior$filters === void 0 ? void 0 : _behavior$filters.map((segmentation) => new PropertyFilter(segmentation))) || [], selectedFiltersOperator: behavior.filtersOperator, extraClass: {
    "for-behaviors": true
  }, onUpdate: (filters, filtersOperator, queryReason) => props.onSelect(produce(props.selected, (item) => {
    item.behavior.filters = filters.map((filter) => filter.getAttributes());
    item.behavior.filtersOperator = filtersOperator;
  }), queryReason), sectionParams: {
    dataGroupsEnabled: false,
    allowCohortFilter: false,
    canRenameStep: false,
    projectHasPeople: false,
    projectHasCohorts: false,
    hideEvents: false,
    includeCustomProperties: IncludeCustomPropertyType.All
  }, hideEvents: !isEventBasedEntry(props.entry), hasNewUI: props.hasNewUI, sectionProps: props.sectionProps, nestedSectionParams: props.nestedSectionParams, placeholders: props.placeholder, onAbortStage: () => props.setActivePart(EntryPart.Filters, "close"), "data-sentry-element": "RowFilters", "data-sentry-component": "BehavioralSubFilters", "data-sentry-source-file": "template.tsx" });
}
export function RevenueSubItem(props) {
  var _selected$behavior$pr;
  const selected = props.selected;
  return /* @__PURE__ */ React.createElement("div", { className: "mp-query-sub-selector", "data-sentry-component": "RevenueSubItem", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ React.createElement(PropertySelector, { ...props, isInner: true, contextPanelParentElement: props.contextPanelParentElement, onSelect: (selection) => {
    const handledEvent = _objectSpread(_objectSpread({}, selected), {}, {
      behavior: _objectSpread(_objectSpread({}, selected.behavior), {}, {
        property: selection.item
      })
    });
    props.onSelect(handledEvent);
  }, isPlaceholder: props.isPlaceholder, queryBlockConfig: _objectSpread(_objectSpread({}, props.queryBlockConfig), {}, {
    type: MenuType.Properties,
    hideEvents: true,
    menuItemTypes: [MenuItemType.SCDProperty],
    otherGroupByOptions: [],
    otherFilterOptions: [],
    showAllTab: false,
    supportB2bCohorts: false
  }), selected: propertyItemToSegmentation(selected.behavior.property, (_selected$behavior$pr = selected.behavior.property) === null || _selected$behavior$pr === void 0 ? void 0 : _selected$behavior$pr.resourceType), "data-sentry-element": "PropertySelector", "data-sentry-source-file": "template.tsx" }), props.hasNewUI && /* @__PURE__ */ React.createElement(RevenueChangeBlock, { behavior: selected.behavior, onChange: (updatedBehavior) => {
    props.onSelect(_objectSpread(_objectSpread({}, selected), {}, {
      behavior: updatedBehavior
    }));
  } }));
}
